import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import {
  generateApplicationUrl,
  getQueryParamsFromHash,
  launchModal,
  parsePermittedQueryParams,
} from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as trainingLessonActions from '@redux/modules/trainingLesson'
import { loadTrainingModule } from '@redux/modules/trainingModule'

import defaultFormState, { requiredFields } from '@models/trainingLesson'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type {
  TrainingLessonModel,
  TrainingLessonRequestOptions,
  TrainingModuleModel,
} from '@models/types'

export const generateUrls = (
  trainingLesson?: Partial<TrainingLessonModel>,
  trainingModule?: Partial<TrainingModuleModel>,
) => {
  const { id, training_module_id } = trainingLesson || {}
  const { training_course_id } = trainingModule || {}

  // @ts-expect-error TS2339
  const { applicationUrl } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
  })

  return {
    editTrainingLessonUrl: `#/systemManager/trainingLessons/${id}/edit/`,
    linkUrl:
      `${applicationUrl}/training/dashboard#/courses/${training_course_id}/modules/${training_module_id}/lessons/${id}`,
    trainingLessonsIndexUrl: '#/systemManager/trainingLessons',
  }
}

type CreateTrainingLessonParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingLessonRequestOptions,
  trainingLessonParams: Partial<TrainingLessonModel>,
}

const createTrainingLesson = (params: CreateTrainingLessonParams) => {
  const { dispatch, requestOptions, trainingLessonParams } = params
  const { createTrainingLesson: createFn } = trainingLessonActions

  return dispatch(createFn(trainingLessonParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editTrainingLessonUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editTrainingLessonUrl,
        success,
      }
    }

    return response
  })
}

type UpdateTrainingLessonParams = {
  dispatch: AppDispatch,
  requestOptions?: TrainingLessonRequestOptions,
  trainingLesson: TrainingLessonModel,
  trainingLessonParams: Partial<TrainingLessonModel>,
}

const updateTrainingLesson = (params: UpdateTrainingLessonParams) => {
  const {
    dispatch, requestOptions, trainingLesson, trainingLessonParams,
  } = params
  const { updateTrainingLesson: updateFn } = trainingLessonActions

  const updatedParams = {
    id: trainingLesson.id,
    ...trainingLessonParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTrainingLessonParams = {
  dispatch: AppDispatch,
  trainingLesson: DeleteParams<TrainingLessonModel>,
}

const deleteTrainingLesson = (params: DeleteTrainingLessonParams) => {
  const { dispatch, trainingLesson } = params
  const { deleteTrainingLesson: deleteFn } = trainingLessonActions

  return dispatch(deleteFn(trainingLesson)).then((response) => {
    const { success } = response

    if (success){
      const { editTrainingLessonUrl } = generateUrls()
      const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

      dispatch(loadTrainingModule(
        // @ts-expect-error TS2345
        trainingLesson.training_module_id,
        { include_training_module_lesson_count: true },
      ))

      return {
        ...response,
        redirectUrl: redirect || editTrainingLessonUrl,
      }
    }
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTrainingLessonForm(
  trainingLesson: Partial<TrainingLessonModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const trainingLessonForm = useForm(
    defaultFormState,
    { entity: trainingLesson, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [trainingLesson.id, trainingLesson.cache_key],
  )

  return {
    ...trainingLessonForm,
  }
}

export function useRelations(trainingLesson: TrainingLessonModel) {
  const { training_module_id } = trainingLesson || {}

  const trainingModules = useSelector(reduxState => reduxState.entities.trainingModules)

  const trainingModule = trainingModules[training_module_id] || {}

  return {
    trainingModule,
  }
}

function useTrainingLesson(initEntity: Partial<TrainingLessonModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: trainingLesson }: { entity: TrainingLessonModel } = useLatestEntity(initEntity, 'trainingLessons')

  const dispatch = useDispatch()

  const { trainingModule } = useRelations(trainingLesson)

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loaded, loading, updating,
  } = useSelector(reduxState => reduxState.trainingLessons)

  return {
    callbacks: {
      addTrainingLesson: () => launchModal({
        callbacks,
        modalKey: 'CreateTrainingLessonModal',
        payload: { trainingLesson },
      }),
      createTrainingLesson: (
        trainingLessonParams: Partial<TrainingLessonModel>,
        entityOptions?: TrainingLessonRequestOptions,
      ) => createTrainingLesson({ trainingLessonParams, dispatch, requestOptions: entityOptions }),
      deleteTrainingLesson: () => deleteTrainingLesson({ dispatch, trainingLesson }),
      updateTrainingLesson: (
        trainingLessonParams: Partial<TrainingLessonModel>,
        entityOptions?: TrainingLessonRequestOptions,
      ) => updateTrainingLesson({
        trainingLesson, trainingLessonParams, dispatch, requestOptions: entityOptions,
      }),
    },
    creating,
    deleting,
    loaded,
    loading,
    trainingLesson,
    updating,
    urls: generateUrls(trainingLesson, trainingModule),
  }
}

export default useTrainingLesson
